import { Box, Button, CircularProgress, MenuItem, Paper, TextField, Theme, Typography } from '@mui/material';
import createStyles from '@mui/styles/createStyles';
import makeStyles from '@mui/styles/makeStyles';
import React, { Fragment, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ICheckpoint } from '../../../models/IEvent';
import { IReaderAdmin } from '../../../models/IReader';
import ReaderServiceInstance from '../../../services/ReaderService';
import DelayEditor from '../../common/DelayEditor';
import IEventBaseDataProps from './IEventBaseDataProps';
import HelpTooltip from '../../common/HelpTooltip';

const DATE_TIME_FORMAT_OPTIONS: Intl.DateTimeFormatOptions = {
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
};

const ISO_TIMESTAMP_SUBSECONDS_PATTERN = /(\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2})\.\d+/;

function formatReaderTimestamp(value: string): string {
    return value.replace(ISO_TIMESTAMP_SUBSECONDS_PATTERN, '$1');
}

function formatServerTimestamp(value: string): string {
    const date = new Date(value);
    if (isNaN(date.getTime())) {
        console.warn(`formatServerTimestamp: could not parse date string "${value}"`);
        return value;
    }
    return date.toLocaleString([], DATE_TIME_FORMAT_OPTIONS);
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        table: {
            minWidth: 280,
        },
        root: {
            width: '100%',
        },
        paper: {
            width: '100%',
            marginBottom: theme.spacing(2),
            padding: theme.spacing(2),
        },
        delayRow: {
            display: 'flex',
            alignItems: 'center',
            gap: theme.spacing(1),
            marginTop: theme.spacing(2),
        },
        refreshButton: {
            marginBottom: theme.spacing(2.5),
        },
        readerSelect: {
            flex: 1,
        },
    }));

interface IEventReadersData {
    checkpoints: ICheckpoint[];
    readers: string[];
}
export default function EventReaders(props: IEventBaseDataProps<IEventReadersData>) {
    const classes = useStyles();
    const { t } = useTranslation("common");
    const data = props.data;

    const [readerDetails, setReaderDetails] = useState<IReaderAdmin[]>([]);
    const [delayValue, setDelayValue] = useState<number | ''>('');
    const [updatingDelay, setUpdatingDelay] = useState(false);
    const [suggestingDelay, setSuggestingDelay] = useState(false);
    const [refreshing, setRefreshing] = useState(false);

    const selectedReaderId = data.checkpoints.length > 0 ? data.checkpoints[0].readerId : '';

    const fetchReaderDetails = useCallback(() => {
        if (data.readers.length === 0) return;
        setRefreshing(true);
        ReaderServiceInstance.getReaders().then(allReaders => {
            const relevant = allReaders.filter(r => data.readers.includes(r.readerId));
            setReaderDetails(relevant);
        }).catch(e => console.error(e)).finally(() => setRefreshing(false));
    }, [data.readers]);

    useEffect(() => {
        fetchReaderDetails();
    }, [fetchReaderDetails]);

    useEffect(() => {
        if (!selectedReaderId) {
            setDelayValue('');
            return;
        }
        const reader = readerDetails.find(r => r.readerId === selectedReaderId);
        setDelayValue(reader?.estimatedDelayMs ?? '');
    }, [selectedReaderId, readerDetails]);

    const triggerDataChange = (newData: IEventReadersData) => {
        if (props.onDataChange) {
            props.onDataChange(newData);
        }
    }

    const handleUpdateDelay = async () => {
        if (!selectedReaderId || delayValue === '') return;
        setUpdatingDelay(true);
        try {
            await ReaderServiceInstance.updateReaderDelay(selectedReaderId, delayValue as number);
            setReaderDetails(prev =>
                prev.map(r => r.readerId === selectedReaderId ? { ...r, estimatedDelayMs: delayValue as number } : r)
            );
        } catch (e) {
            console.error(e);
        } finally {
            setUpdatingDelay(false);
        }
    }

    const handleSuggestDelay = async () => {
        if (!selectedReaderId) return;
        setSuggestingDelay(true);
        try {
            const suggested = await ReaderServiceInstance.getSuggestedDelay(selectedReaderId);
            setDelayValue(suggested);
        } catch (e) {
            console.error(e);
        } finally {
            setSuggestingDelay(false);
        }
    }

    return (<Fragment>
        <Paper className={classes.paper}>
            <Box display="flex" alignItems="center" gap={1}>
                <TextField
                    id="checkpoints"
                    select
                    label={<>{t("eventsetup.checkpoints")}<HelpTooltip text={t("tooltips.readerSelection")} /></>}
                    value={selectedReaderId}
                    onChange={e => {
                        if (e.target.value.length > 0) {
                            triggerDataChange({
                                ...data,
                                checkpoints: [{
                                    readerId: e.target.value
                                }]
                            });
                        }
                    }}
                    helperText={t("eventsetup.checkpointshelper")}
                    className={classes.readerSelect}
                >
                    {data.readers.map((reader) => (
                        <MenuItem
                            key={reader}
                            value={reader}>
                            {reader}
                        </MenuItem>
                    ))}
                </TextField>
                <Button
                    variant="outlined"
                    size="small"
                    onClick={fetchReaderDetails}
                    disabled={refreshing}
                    startIcon={refreshing ? <CircularProgress size={16} /> : undefined}
                    className={classes.refreshButton}
                >
                    {t("eventsetup.refreshReaders")}
                </Button>
            </Box>
            {selectedReaderId && (
                <Box className={classes.delayRow}>
                    <Box>
                        <Box display="flex" alignItems="center" mb={0.5}>
                            <Typography variant="body2" color="textSecondary">
                                {t("eventsetup.readerDelay")}
                            </Typography>
                            <HelpTooltip text={t("tooltips.readerDelay")} />
                        </Box>
                        <DelayEditor
                            valueMs={delayValue !== '' ? delayValue as number : 0}
                            onChange={ms => setDelayValue(ms)}
                        />
                    </Box>
                    <Box display="flex" flexDirection="column" gap={1}>
                        <Button
                            variant="outlined"
                            size="small"
                            onClick={handleUpdateDelay}
                            disabled={delayValue === '' || updatingDelay}
                            startIcon={updatingDelay ? <CircularProgress size={16} /> : undefined}
                        >
                            {t("eventsetup.updateDelay")}
                        </Button>
                        <Button
                            variant="outlined"
                            size="small"
                            onClick={handleSuggestDelay}
                            disabled={suggestingDelay}
                            startIcon={suggestingDelay ? <CircularProgress size={16} /> : undefined}
                        >
                            {t("eventsetup.suggestDelay")}
                        </Button>
                    </Box>
                </Box>
            )}
            {selectedReaderId && (() => {
                const reader = readerDetails.find(r => r.readerId === selectedReaderId);
                if (!reader) return null;
                return (
                    <Box mt={1}>
                        <Typography variant="body2" color="textSecondary">
                            {reader.manufacturer} {reader.model}{reader.description ? ` - ${reader.description}` : ''}
                        </Typography>
                        {reader.lastSeen && (
                            <Typography variant="body2" color="textSecondary">
                                {t("eventsetup.readerLastSeen")}: {formatReaderTimestamp(reader.lastSeen)}
                            </Typography>
                        )}
                        {reader.lastSeenServer && (
                            <Typography variant="body2" color="textSecondary">
                                {t("eventsetup.readerLastSeenServer")}: {formatServerTimestamp(reader.lastSeenServer)}
                            </Typography>
                        )}
                    </Box>
                );
            })()}
        </Paper>
    </Fragment>);
}